/**
 * Numbered process-flow diagram showing the 7-step shipment tracking lifecycle
 * for a disposable smart label: activate → attach → dispatch → detect →
 * transmit → monitor → complete → dispose. Pure SVG so it prints and scales.
 */
import React from "react";

const STEPS = [
  { n: 1, k: "Activate",  d: "Pull-tab or peel." },
  { n: 2, k: "Attach",    d: "To parcel / carton / pallet." },
  { n: 3, k: "Dispatch",  d: "Shipment enters the lane." },
  { n: 4, k: "Detect",    d: "Position + sensors sample." },
  { n: 5, k: "Transmit",  d: "Via BLE gateway or cellular." },
  { n: 6, k: "Monitor",   d: "Live view + exception alerts." },
  { n: 7, k: "Complete",  d: "End-of-journey; dispose responsibly." },
];

export default function ProcessFlow({ title = "How a disposable package tracker moves through one shipment" }) {
  const boxW = 116;
  const gap = 12;
  const startX = 12;
  const y = 76;

  return (
    <figure
      className="my-10 border border-line bg-white p-6 lg:p-8 overflow-x-auto"
      data-testid="process-flow-diagram"
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-widest text-ink-subtle mb-6">
        {title}
      </figcaption>
      <svg
        viewBox="0 0 900 220"
        role="img"
        aria-label="Seven-step process flow showing how a disposable package tracker moves from activation through end-of-journey disposal"
        className="w-full h-auto min-w-[820px]"
      >
        {STEPS.map((s, i) => {
          const x = startX + i * (boxW + gap);
          const isLast = i === STEPS.length - 1;
          return (
            <g key={s.n}>
              <rect
                x={x}
                y={y}
                width={boxW}
                height="90"
                rx="3"
                fill={i === 0 || isLast ? "#0A0A0A" : "#fff"}
                stroke={i === 0 || isLast ? "#0A0A0A" : "#D6D6D2"}
              />
              <text
                x={x + 12}
                y={y + 20}
                fontSize="10"
                fill={i === 0 || isLast ? "#B7B7B0" : "#525252"}
                fontFamily="ui-monospace, SFMono-Regular, monospace"
              >
                0{s.n}
              </text>
              <text
                x={x + 12}
                y={y + 44}
                fontSize="13"
                fill={i === 0 || isLast ? "#fff" : "#0A0A0A"}
                fontFamily="ui-monospace, SFMono-Regular, monospace"
              >
                {s.k}
              </text>
              <text
                x={x + 12}
                y={y + 66}
                fontSize="10"
                fill={i === 0 || isLast ? "#D6D6D2" : "#525252"}
              >
                {s.d}
              </text>
              {i < STEPS.length - 1 && (
                <g>
                  <line
                    x1={x + boxW}
                    y1={y + 45}
                    x2={x + boxW + gap}
                    y2={y + 45}
                    stroke="#0A0A0A"
                    strokeWidth="1"
                  />
                  <polygon
                    points={`${x + boxW + gap - 4},${y + 42} ${x + boxW + gap + 1},${y + 45} ${x + boxW + gap - 4},${y + 48}`}
                    fill="#0A0A0A"
                  />
                </g>
              )}
            </g>
          );
        })}

        {/* Callouts */}
        <text x="12" y="200" fontSize="10" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">
          NO HARDWARE RECOVERY LOOP · NO RECHARGE · NO REVERSE LOGISTICS
        </text>
      </svg>
    </figure>
  );
}
